import React, { useEffect, useRef } from 'react'
import { Dimensions, View } from 'react-native'
import { useTutorNoShow } from '../TutorNoShowContext'
import { DialogM } from '@common/components/alert/dialog/Dialog'
import { theme } from '@common/theme/theme'
import TextM from '@common/components/text/TextM'
import AvatarM from '@common/components/avatar/AvatarM'
import { IconImageM } from '@common/components/icons/IconsM'
import live from '../../../../../../assets/icons/generic/live.png'
import { capitalize, formatMinutes } from '@data/utility/Utils'
import moment from 'moment'
import { findClosestNextOccurrence } from '../../../../../student/account/onboarding/LiveRoomCard'
import { PlaySoundM } from '@common/components/playSound/PlaySoundM'
import { liveService } from '@data/services/liveService'

/**
 * Modal showing compatible recovery session with confirmation dialogs
 */
export function RecoveryModal() {
    const context = useTutorNoShow()

    // Guard: return null if provider is not present (e.g., trial sessions)
    if (!context) return null

    const {
        isProposing,
        recoverySession,
        isJoining,
        actions,
        liveSession,
        apiRequest,
        skipCleanupRef,
    } = context

    // Use refs to avoid circular dependency issues with useCallback
    const showMainDialogRef = useRef(null)
    const showAcceptConfirmationRef = useRef(null)
    const showDeclineConfirmationRef = useRef(null)

    const maxWidth = Dimensions.get('window').width - 60

    // Define dialog functions
    showAcceptConfirmationRef.current = () => {
        const closestNext = findClosestNextOccurrence([liveSession])
        const payload = {
            recovery_occurrence_id: recoverySession?.id,
            original_occurrence_date: closestNext?.date
                ? moment(closestNext.date).format('YYYY-MM-DD HH:mm')
                : undefined,
        }

        DialogM.show({
            champion: 'abou',
            variant: 'main',
            text1: 'Confirme ta nouvelle séance',
            component: (
                <View style={{ alignSelf: 'stretch', width: '100%', maxWidth }}>
                    <TextM wrap style={{ textAlign: 'center', marginBottom: 15 }}>
                        Tu vas rejoindre un autre live qui a lieu maintenant, à la même heure. Reconnecte-toi dès que tu es prêt.
                    </TextM>
                    <View
                        style={{
                            padding: 12,
                            backgroundColor: theme.colors.warningLight,
                            borderRadius: 10,
                            borderLeftWidth: 4,
                            borderLeftColor: theme.colors.warning,
                        }}
                    >
                        <TextM
                            wrap
                            fontSize={13}
                            style={{ textAlign: 'center' }}
                        >
                            💡 N'hésite pas à en parler avec tes parents avant de confirmer !
                        </TextM>
                    </View>
                </View>
            ),
            buttonText1: 'OK, j\'y vais',
            buttonColor1: theme.colors.success,
            buttonLoading1: isJoining,
            buttonDisabled1: isJoining,
            onPress: () => {
                // Set flag BEFORE async call to prevent cleanup race condition
                if (skipCleanupRef) skipCleanupRef.current = true
                actions.joinRecovery()
                apiRequest(liveService.autoRecoveryTutorLate, { id: liveSession?.id }, payload)
            },
            buttonText2: 'Retour',
            buttonColor2: theme.colors.grey,
            onPressBack: () => {
                setTimeout(() => showMainDialogRef.current?.(), 100)
            },
            preventAutoClose: true,
            exitButton: true,
            onExitButtonPress: () => {
                setTimeout(() => showMainDialogRef.current?.(), 100)
            },
        })
    }

    showDeclineConfirmationRef.current = () => {
        DialogM.show({
            champion: 'abou',
            variant: 'main',
            text1: 'Rattraper plus tard',
            component: (
                <View style={{ alignSelf: 'stretch', width: '100%', maxWidth }}>
                    <TextM wrap style={{ textAlign: 'center', marginBottom: 15 }}>
                        Tu vas recevoir un mail pour réserver une séance de rattrapage un autre jour.
                    </TextM>
                    <TextM wrap style={{ textAlign: 'center', marginBottom: 15 }}>
                        Tes parents seront également prévenus.
                    </TextM>
                    <View
                        style={{
                            padding: 12,
                            backgroundColor: theme.colors.warningLight,
                            borderRadius: 10,
                            borderLeftWidth: 4,
                            borderLeftColor: theme.colors.warning,
                        }}
                    >
                        <TextM
                            wrap
                            fontSize={13}
                            style={{ textAlign: 'center' }}
                        >
                            💡 N'hésite pas à en parler avec tes parents avant de confirmer !
                        </TextM>
                    </View>
                </View>
            ),
            buttonText1: 'Confirmer et quitter',
            buttonColor1: theme.colors.primary,
            onPress: () => {
                DialogM.hide()
                actions.preferCredit()
            },
            buttonText2: 'Retour',
            buttonColor2: theme.colors.grey,
            onPressBack: () => {
                setTimeout(() => showMainDialogRef.current?.(), 100)
            },
            preventAutoClose: true,
            exitButton: true,
            onExitButtonPress: () => {
                setTimeout(() => showMainDialogRef.current?.(), 100)
            },
        })
    }

    showMainDialogRef.current = () => {
        DialogM.show({
            champion: 'abou',
            variant: 'unhappy',
            text1: "Ton tuteur n'a pas pu venir",
            component: (
                <View style={{ alignSelf: 'stretch', width: '100%', maxWidth }}>
                    <TextM wrap style={{ textAlign: 'center', marginBottom: 20 }}>
                        {'On t\'installe tout de suite dans un autre live qui commence à la même heure, avec un tuteur disponible — tu ne perds pas ta séance.'}
                    </TextM>
                    {/* Session Card */}
                    <View
                        style={{
                            borderRadius: 15,
                            overflow: 'hidden',
                            borderColor: theme.colors.lightMore,
                            backgroundColor: theme.colors.white,
                            borderWidth: 1,
                            shadowColor: '#000',
                            shadowOffset: { width: 0, height: 2 },
                            shadowOpacity: 0.1,
                            shadowRadius: 8,
                            elevation: 4,
                            marginVertical: 10,
                        }}
                    >
                        {/* Header */}
                        <View
                            style={{
                                backgroundColor: theme.colors.success,
                                paddingHorizontal: 15,
                                paddingVertical: 12,
                                flexDirection: 'row',
                                alignItems: 'center',
                                borderBottomWidth: 1,
                                borderColor: theme.colors.lightMore,
                            }}
                        >
                            <IconImageM
                                source={live}
                                width={26}
                                style={{ tintColor: theme.colors.white }}
                            />
                            <TextM
                                fontWeight="SemiBold"
                                color={theme.colors.white}
                                style={{ marginLeft: 8, flex: 1 }}
                                wrap
                            >
                                {capitalize(
                                    moment(recoverySession?.date).format('dddd DD MMM [à] HH:mm') +
                                    ' - ' +
                                    formatMinutes(recoverySession?.duration || 120)
                                )}
                            </TextM>
                        </View>

                        {/* Tutor Info */}
                        <View style={{ paddingHorizontal: 15, paddingVertical: 15 }}>
                            <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                                <AvatarM
                                    size={36}
                                    user={recoverySession?.tutor?.avatar}
                                    style={{ borderRadius: 30 }}
                                />
                                <View style={{ marginLeft: 12, flex: 1 }}>
                                    <TextM fontWeight="SemiBold" fontSize={15}>
                                        {recoverySession?.tutor?.display_name || 'Ton tuteur'}
                                    </TextM>
                                </View>
                            </View>
                        </View>
                    </View>
                </View>
            ),
            buttonText1: 'Rejoindre ma séance',
            buttonColor1: theme.colors.success,
            onPress: () => {
                setTimeout(() => showAcceptConfirmationRef.current?.(), 100)
            },
            // « Rattraper plus tard » n'est plus un bouton co-égal (il rouvrait une
            // délibération, donc une attente) : sur le dispatch même-heure, rejoindre
            // est l'action mise en avant. Le crédit reste le filet, accessible via la
            // croix de sortie.
            preventAutoClose: true,
            exitButton: true,
            onPressBack: () => {
                setTimeout(() => showDeclineConfirmationRef.current?.(), 100)
            },
            onExitButtonPress: () => {
                setTimeout(() => showDeclineConfirmationRef.current?.(), 100)
            },
        })
    }

    useEffect(() => {
        if (!isProposing || !recoverySession) return

        // Play alert sound to get student's attention
        PlaySoundM.play(require('../../../../../../assets/sounds/question_002.mp3'))

        showMainDialogRef.current?.()

        return () => {
            // Cleanup when component unmounts
            if (isProposing) {
                DialogM.hide()
            }
        }
    }, [isProposing, recoverySession])

    return null // Modal is controlled via DialogM.show
}
